import styled, { css } from 'styled-components';
import {
  MOBILE_BRAND_BAR_CONTENT_HEIGHT_COMPACT_PX,
  MOBILE_BRAND_BAR_CONTENT_HEIGHT_PX,
  SITE_HEADER_CORNER_RADIUS_PX,
} from './HeaderStyles';

const fontFamily = (props) => props.theme?.typography?.body1?.fontFamily || '"Poppins", "Arial", sans-serif';

export { MOBILE_BRAND_BAR_CONTENT_HEIGHT_COMPACT_PX, MOBILE_BRAND_BAR_CONTENT_HEIGHT_PX };

// Fixed top bar with mark + search (mobile only; main header is hidden ≤800px)
export const MobileBrandBar = styled.div`
  display: none;

  @media (max-width: 800px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    gap: 8px;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10009;
    box-sizing: border-box;
    height: calc(${({ $compact }) => (
    $compact ? MOBILE_BRAND_BAR_CONTENT_HEIGHT_COMPACT_PX : MOBILE_BRAND_BAR_CONTENT_HEIGHT_PX
  )}px + env(safe-area-inset-top, 0px));
    padding-top: calc(env(safe-area-inset-top, 0px) + 8px);
    padding-bottom: 12px;
    padding-left: 16px;
    padding-right: 16px;
    background-color: ${(props) => props.theme.palette.background.default};
    border-radius: 0 0 ${SITE_HEADER_CORNER_RADIUS_PX}px ${SITE_HEADER_CORNER_RADIUS_PX}px;
    border: 1px solid ${(props) => props.theme.palette.border?.subtle};
    border-top: none;
    /* Suggestion panel hangs below the bar */
    overflow: visible;
  }

`;

export const MobileBrandBarTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 32px;
  flex-shrink: 0;
`;

export const MobileBrandBarSearch = styled.div`
  width: 100%;
  min-width: 0;
  flex: 1 1 auto;
  overflow: visible;
  position: relative;
  z-index: 1;
`;

export const MobileBrandBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

export const MobileBrandLogoLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-decoration: none;
  line-height: 0;

  img {
    height: 32px;
    width: auto;
    display: block;
  }
`;

/** Mobile tab bar height (padding 4+4 + icon/label ~40). */
export const MOBILE_BOTTOM_NAV_HEIGHT_PX = 48;

/** Gap between the floating tab bar and the screen edges (sides + bottom). */
export const MOBILE_BOTTOM_NAV_INSET_PX = 16;

/** Footer / overlays: breathing room above the occupied tab bar. */
export const MOBILE_BOTTOM_NAV_FOOTER_CLEARANCE = `calc(32px + ${MOBILE_BOTTOM_NAV_HEIGHT_PX + MOBILE_BOTTOM_NAV_INSET_PX}px + env(safe-area-inset-bottom, 0px))`;

/**
 * Occupied height of listing-detail Invite/Apply pills (tab bar + edge inset).
 * Keep in sync with DETAIL_MOBILE_STICKY_FOOTER_PX / INFLUENCER_MOBILE_BOTTOM_BAR_PX.
 */
export const MOBILE_STICKY_CTA_HEIGHT_PX = MOBILE_BOTTOM_NAV_HEIGHT_PX + MOBILE_BOTTOM_NAV_INSET_PX;

/** Footer clearance above fixed Invite/Apply sticky CTAs on listing detail pages. */
export const MOBILE_STICKY_CTA_FOOTER_CLEARANCE = `calc(32px + ${MOBILE_STICKY_CTA_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px))`;

const bottomNavVisibleFlex = css`
  display: flex;
  justify-content: space-around;
  align-items: center;
  gap: clamp(4px, 2vw, 16px);
  min-height: ${MOBILE_BOTTOM_NAV_HEIGHT_PX}px;
`;

// Bottom Navigation — floating pill inset from the screen edges
export const BottomNavWrapper = styled.nav`
  display: none;
  position: fixed;
  bottom: ${(props) => `calc(${props.$insetBottom || `${MOBILE_BOTTOM_NAV_INSET_PX}px`} + env(safe-area-inset-bottom, 0px))`};
  left: ${(props) => props.$insetLeft || `${MOBILE_BOTTOM_NAV_INSET_PX}px`};
  right: ${(props) => props.$insetRight || `${MOBILE_BOTTOM_NAV_INSET_PX}px`};
  background-color: ${(props) => props.theme.palette.background.default};
  border: 1px solid ${(props) => props.theme.palette.border?.subtle || `${props.theme.palette.text.primary}14`};
  border-radius: 999px;
  z-index: ${(props) => props.$zIndex ?? 9999};
  box-sizing: border-box;
  padding: 4px clamp(8px, 3vw, 16px);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  /* Ensure bottom nav never moves on scroll - GPU compositing layer */
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  contain: layout style paint;

  ${(props) => props.$forceVisible && bottomNavVisibleFlex}

  @media (max-width: 800px) {
    ${bottomNavVisibleFlex}
  }
`;

export const BottomNavItem = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1 1 0;
  max-width: min(112px, 28vw);
  min-width: 0;
  padding: clamp(2px, 0.8vw, 4px) clamp(4px, 1.5vw, 8px);
  appearance: none;
  background: transparent;
  border: none;
  font: inherit;
  text-decoration: none;
  color: ${(props) => props.theme.palette.text.secondary};
  transition: color 0.2s ease;
  cursor: pointer;
  position: relative;

  &:hover {
    color: ${(props) => props.theme.palette.primary.main};
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    pointer-events: none;
  }

  ${(props) => props.$static
    && css`
      cursor: default;
      pointer-events: none;

      &:hover {
        color: inherit;
      }

      &:active {
        transform: none;
      }
    `}

  ${(props) => props.$isActive
    && css`
      &,
      &:link,
      &:visited,
      &:hover {
        color: ${props.theme.palette.accent.main};
      }
    `}
`;

export const BottomNavIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: clamp(15px, 4.2vw, 17px);
  margin-bottom: 1px;
  width: clamp(15px, 4.2vw, 17px);
  height: clamp(15px, 4.2vw, 17px);
  color: inherit;
  transition: color 0.2s ease;

  svg {
    width: 100%;
    height: 100%;
    stroke-width: 1.5;
    color: inherit;
  }
`;

export const BottomNavLabel = styled.span`
  font-size: var(--font-micro);
  font-weight: 400;
  font-family: ${fontFamily};
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  line-height: 1.15;
  color: inherit;
`;
